import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { CheckCircle2, AlertTriangle, Info, X, AlertCircle } from 'lucide-react'

const VARIANTS = {
  success: {
    icon: CheckCircle2,
    color: 'text-emerald-600',
    bg: 'bg-emerald-50',
    border: 'border-emerald-200',
  },
  error: {
    icon: AlertCircle,
    color: 'text-red-600',
    bg: 'bg-red-50',
    border: 'border-red-200',
  },
  warning: {
    icon: AlertTriangle,
    color: 'text-amber-600',
    bg: 'bg-amber-50',
    border: 'border-amber-200',
  },
  info: {
    icon: Info,
    color: 'text-primary-500',
    bg: 'bg-primary-50',
    border: 'border-primary-200',
  },
}

const ToastContext = createContext(null)

let toastIdCounter = 0

export function ToastProvider({ children, defaultDuration = 5000 }) {
  const [toasts, setToasts] = useState([])
  const timersRef = useRef(new Map())

  const dismiss = useCallback((id) => {
    setToasts((prev) => prev.filter((t) => t.id !== id))
    const timer = timersRef.current.get(id)
    if (timer) {
      clearTimeout(timer)
      timersRef.current.delete(id)
    }
  }, [])

  const push = useCallback((variant, message, opts = {}) => {
    const id = ++toastIdCounter
    const duration = opts.duration ?? defaultDuration
    setToasts((prev) => [
      ...prev,
      {
        id,
        variant,
        message,
        title: opts.title,
        action: opts.action,
      },
    ])
    if (duration > 0) {
      const t = setTimeout(() => dismiss(id), duration)
      timersRef.current.set(id, t)
    }
    return id
  }, [defaultDuration, dismiss])

  useEffect(() => {
    return () => {
      timersRef.current.forEach((t) => clearTimeout(t))
      timersRef.current.clear()
    }
  }, [])

  const api = useMemo(() => ({
    success: (msg, opts) => push('success', msg, opts),
    error: (msg, opts) => push('error', msg, opts),
    warning: (msg, opts) => push('warning', msg, opts),
    info: (msg, opts) => push('info', msg, opts),
    dismiss,
  }), [push, dismiss])

  return (
    <ToastContext.Provider value={api}>
      {children}
      {createPortal(
        <div
          aria-live="polite"
          aria-atomic="true"
          className="fixed top-4 right-4 z-[60] flex flex-col gap-2 w-[calc(100vw-2rem)] sm:w-96 max-w-full pointer-events-none"
        >
          {toasts.map((t) => (
            <ToastItem key={t.id} toast={t} onDismiss={() => dismiss(t.id)} />
          ))}
        </div>,
        document.body
      )}
    </ToastContext.Provider>
  )
}

function ToastItem({ toast, onDismiss }) {
  const meta = VARIANTS[toast.variant] || VARIANTS.info
  const Icon = meta.icon
  return (
    <div
      role={toast.variant === 'error' ? 'alert' : 'status'}
      className={`pointer-events-auto flex items-start gap-3 rounded-lg border ${meta.border} ${meta.bg} bg-white shadow-lg p-3 animate-fade-in`}
    >
      <Icon className={`h-5 w-5 ${meta.color} flex-shrink-0 mt-0.5`} aria-hidden="true" />
      <div className="flex-1 min-w-0">
        {toast.title && (
          <p className="text-sm font-semibold text-gray-900">{toast.title}</p>
        )}
        <p className="text-sm text-gray-700 break-words">{toast.message}</p>
        {toast.action && (
          <button
            type="button"
            onClick={() => {
              try { toast.action.onClick?.() } finally { onDismiss() }
            }}
            className="mt-1.5 text-sm font-semibold text-primary-600 hover:text-primary-700"
          >
            {toast.action.label}
          </button>
        )}
      </div>
      <button
        type="button"
        onClick={onDismiss}
        aria-label="Dismiss notification"
        className="text-gray-400 hover:text-gray-600 p-0.5 -m-0.5 rounded focus:outline-none focus:ring-2 focus:ring-primary-500"
      >
        <X className="h-4 w-4" />
      </button>
    </div>
  )
}

export function useToast() {
  const ctx = useContext(ToastContext)
  if (!ctx) {
    throw new Error('useToast must be used within <ToastProvider>')
  }
  return ctx
}

export default ToastProvider
